import {ApplicationConfig, importProvidersFrom, Optional, PLATFORM_ID, TransferState} from '@angular/core';
import {provideRouter, Routes, withComponentInputBinding, withDisabledInitialNavigation} from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { NgbDateAdapter, NgbDateNativeAdapter } from '@ng-bootstrap/ng-bootstrap';
import { ToastrModule } from 'ngx-toastr';
import { NgxSpinnerModule } from 'ngx-spinner';
import { NgxTurnstileModule, NgxTurnstileFormsModule } from 'ngx-turnstile';
import { SwUpdate, provideServiceWorker } from '@angular/service-worker';
import { DateAdapter, CalendarDateFormatter, CalendarEventTitleFormatter, CalendarNativeDateFormatter, CalendarAngularDateFormatter, CalendarUtils, CalendarModule } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import {isPlatformBrowser, registerLocaleData} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeFr from '@angular/common/locales/fr';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeEs from '@angular/common/locales/es';
import localeEsExtra from '@angular/common/locales/extra/es';
import localePt from '@angular/common/locales/pt';
import localePtExtra from '@angular/common/locales/extra/pt';
import localeEn from '@angular/common/locales/en';
import localeEnExtra from '@angular/common/locales/extra/en';
import {TranslateService, TranslateModule, provideTranslateService, TranslateLoader} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LocalizeRouterService, LocalizeParser, LocalizeRouterSettings, withLocalizeRouter, ManualParserLoader, CacheMechanism } from '@gilsdav/ngx-translate-router';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { isDevMode } from '@angular/core';
import { inject } from '@angular/core';
import { routes } from './app.routes';
import { jwtInterceptor } from './shared/interceptors/jwt.interceptor';
import { errorInterceptor } from './shared/interceptors/error.interceptor';
import { loadingInterceptor } from './shared/interceptors/loading.interceptor';
import { AuthService } from './services/auth.service';
import { AuthGuard } from './services/auth-guard.service';
import { MessageService } from './services/message.service';
import { LeafletService } from './services/leaflet.service';
import { BookingService } from './services/booking.service';
import { LocationService } from './services/location.service';
import { DataService } from './services/data.service';
import { CanonicalAndMetaService } from './services/canonical-and-meta.service';
import { RateService } from './services/rate.service';
import { VehiclesService } from './services/vehicles.service';
import { LoaderService } from './services/loader.service';
import { SurveyService } from './services/survey.service';
import { StaffService } from './services/staff.service';
import { TestimonialsService } from './services/testimonials.service';
import { NewsService } from './services/news.service';
import { InstagramService } from './services/instagram.service';
import { JobService } from './services/job.service';
import { PaymentsService } from './services/payments.service';
import { EventService } from './services/event.service';
import { HandleUnrecoverableStateService } from './services/service.worker/handle-unrecoverable-state.service';
import { PromptUpdateService } from './services/service.worker/prompt-update.service';
import { LogUpdateService } from './services/service.worker/log-update.service';
import { CheckForUpdateService } from './services/service.worker/check-for-update.service';
import { environment } from '../environments/environment';
import { APP_INITIALIZER } from '@angular/core';
import { REQUEST } from '../express.tokens';
import {translateLoaderFactory} from './i18n/translate-loaders';
import {InvoiceService} from './services/invoice.service';
// Register locales
registerLocaleData(localeDe, 'de', localeDeExtra);
registerLocaleData(localeFr, 'fr', localeFrExtra);
registerLocaleData(localeEn, 'en', localeEnExtra);
registerLocaleData(localeEs, 'es', localeEsExtra);
registerLocaleData(localePt, 'pt', localePtExtra);


// Create a custom parser factory function to ensure locales are properly initialized
export function createTranslateRouteLoader(translate: TranslateService, location: Location, settings: LocalizeRouterSettings) {
  const parser = new ManualParserLoader(translate, location, settings);

  // Ensure the locales array is initialized
  parser.locales = ['en', 'es', 'fr', 'de', 'pt'];

  return parser;
}

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader =
  (http: HttpClient) => new TranslateHttpLoader(http, './i18n/', '.json');

export const appConfig: ApplicationConfig = {
  providers: [
    // I18n
    provideHttpClient(
      withFetch(),
      withInterceptors([
        jwtInterceptor,
        errorInterceptor,
        loadingInterceptor
      ])
    ),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: translateLoaderFactory,
        deps: [HttpClient, TransferState, PLATFORM_ID]
      }
    }),
    // Initialize TranslateService with supported languages
    {
      provide: APP_INITIALIZER,
      useFactory: (translate: TranslateService) => {
        return () => {
          // Add supported languages
          translate.addLangs(['en', 'es', 'fr', 'de', 'pt']);
          // Set default language
          translate.setDefaultLang('en');
          return translate;
        };
      },
      deps: [TranslateService],
      multi: true
    },
    importProvidersFrom(
      CalendarModule.forRoot({
        provide: DateAdapter,
        useFactory: adapterFactory
      }),
      NgxTurnstileModule,
      NgxTurnstileFormsModule
    ),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withLocalizeRouter(routes, {
        parser: {
          provide: LocalizeParser,
          useFactory: createTranslateRouteLoader,
          deps: [TranslateService, Location, LocalizeRouterSettings]
        },
        useCachedLang: true,
        alwaysSetPrefix: true,
        cacheMechanism: CacheMechanism.Cookie,
        cookieFormat: '{{value}};{{expires}};path=/',
      })
    ),
    provideClientHydration(),
    provideAnimations(),
    { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false }
    },
    // Services
    AuthService,
    AuthGuard,
    MessageService,
    LeafletService,
    BookingService,
    LocationService,
    DataService,
    CanonicalAndMetaService,
    RateService,
    VehiclesService,
    LoaderService,
    SurveyService,
    StaffService,
    TestimonialsService,
    NewsService,
    InstagramService,
    JobService,
    PaymentsService,
    EventService,
    HandleUnrecoverableStateService,
    PromptUpdateService,
    LogUpdateService,
    CheckForUpdateService,
    LocalizeRouterService,
    // Service Worker Configuration
    SwUpdate,
    provideServiceWorker('ngsw-worker.js', {
      enabled: true,
      registrationStrategy: 'registerWhenStable:30000'
    })
  ]


};
